import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

const Dialog = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Root>,
  React.ComponentProps<typeof DialogPrimitive.Root>
>(({ ...props }) => <DialogPrimitive.Root modal={true} {...props} />);
Dialog.displayName = DialogPrimitive.Root.displayName;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-[#000000f5] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    gallery?: boolean;
  }
>(({ className, children, gallery, ...props }, ref) => {
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={(e) => {
          //  cancel Radix's default "first focusable" behavior
          e.preventDefault();
          //  blur the previously focused input (hides mobile keyboard)
          // biome-ignore lint/style/noRestrictedGlobals: <explanation>
          (document?.activeElement as HTMLElement | null)?.blur();
          // focus a neutral, *visible* element in the dialog (instead of a random control)
          // rAF improves iOS safari reliability
          requestAnimationFrame(() => {
            bodyRef.current?.focus({ preventScroll: true });
          });
        }}
        className={cn(
          "fixed h-fit m-auto inset-0 z-50 duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          gallery
            ? "w-fit max-sm:my-0"
            : "max-sm:m-6 rounded-lg bg-background border-line-color border shadow-lg sm:w-[400px] lg:w-[500px]",
          className,
        )}
        {...props}
      >
        {/* content wrapper */}
        <div
          // neutral focus target: visible, programmatically focusable, not tabbable
          ref={bodyRef}
          tabIndex={-1}
          className={cn(
            "flex outline-none h-fit max-h-screen w-full overflow-y-auto flex-col p-6 justify-between",
            gallery && "max-sm:h-screen max-sm:w-screen",
          )}
        >
          {children}
        </div>
        {/* dont show x close button on galleries */}
        {!gallery && (
          <DialogPrimitive.Close className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 data-[state=open]:text-slate-500 dark:ring-offset-slate-950 dark:focus:ring-slate-300 dark:data-[state=open]:bg-slate-800 dark:data-[state=open]:text-slate-400">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogBody = ({
  className,
  gallery,
  children,
}: React.HTMLAttributes<HTMLDivElement> & { gallery?: boolean }) => {
  return (
    <div
      className={cn(
        "mb-4",
        gallery &&
          "max-sm:mb-40 flex flex-col items-center justify-center sm:flex-row sm:items-start",
        className,
      )}
    >
      {children}
    </div>
  );
};
DialogBody.displayName = "DialogBody";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  gallery,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { gallery?: boolean }) => (
  <div
    className={cn(
      "animate-in fade-in duration-200 flex flex-row-reverse sm:space-x-2 border-t pt-4 border-line-color gap-2",
      gallery &&
        "max-sm:fixed max-sm:bottom-0 max-sm:left-0 max-sm:bg-secondary max-sm:w-full max-sm:p-4",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("underline mb-4 leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-slate-500 dark:text-slate-400", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
